import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Field, Form, useFormikContext } from 'formik';
import { Trans } from 'react-i18next';

import { requestService } from 'shared/utils/services/requestService';
import { Button } from 'shared/components/Button';
import { FormikInput } from 'shared/components/FormikInput';
import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { variableFontRegular } from 'styles/theme/text';
import { IGiftCardFormValues } from 'checkout/components/Payment/GiftCard';
import { GIFT_CARD_CODE_UNSANITIZED_LENGTH } from 'checkout/enums/GiftCard';
import { formatGiftCardCode } from 'shared/utils/formatGiftCardCode';
import { useCurrency } from 'shared/hooks/useCurrency';
import { IGiftCardValidateResponse } from 'checkout/types/GiftCard';
import { t } from 'localization/utils/t';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';

const TP = 'checkout.components.Payment.GiftCardForm';

export const GiftCardForm = () => {
  const { values, setFieldValue, errors, validateForm, setFieldError, submitForm } =
    useFormikContext<IGiftCardFormValues>();
  const { selectedCurrencyCode } = useCurrency();

  const GIFT_CARD_CODE_LABEL = t(`${TP}.giftCardCodeLabel`, 'Gift Card Code');
  const ENTER_CARD_LABEL = t(`${TP}.enterCodeLable`, 'Enter Code');

  const [validating, setValidating] = useState(false);
  const [foreignCurrencyAmountCents, setfForeignCurrencyAmountCents] = useState<number>(null);
  const [inputLabel, setInputLabel] = useState<string>(GIFT_CARD_CODE_LABEL);

  const validate = async () => {
    if (!values.giftCardCode) return;

    const { giftCardCode: giftCardError = '' } = await validateForm();

    if (giftCardError) {
      setValidating(false);
      return;
    }

    const strippedGiftCardCode = values.giftCardCode.split('-').join('').toUpperCase();

    try {
      const response: IGiftCardValidateResponse = await requestService({
        url: '/web-api/v1/gift-cards/validate',
        data: { giftCardCode: strippedGiftCardCode },
        method: 'POST',
      });

      if (selectedCurrencyCode !== 'USD') {
        setfForeignCurrencyAmountCents(response?.giftCardBalance?.amountCents);
      }

      void setFieldValue('giftCardValue', response?.giftCardBalance?.amountCentsUsd);
      void setFieldValue('giftCardId', response?.giftCardId);
    } catch {
      setFieldError('giftCardCode', t(`${TP}.invalidGiftCardCode`, 'Invalid Gift Card Code'));
    } finally {
      setValidating(false);
    }
  };

  /**
   * NOTE: handles change event of gift card code. there is an issue with our
   * formik input component that does not handle on change correctly
   */
  useEffect(() => {
    if (!values?.giftCardCode) return;

    void setFieldValue('giftCardCode', formatGiftCardCode(values?.giftCardCode));
    void setFieldValue('giftCardValue', null);

    if (values?.giftCardCode?.length < GIFT_CARD_CODE_UNSANITIZED_LENGTH || validating) return;

    setValidating(true);
    void validate();
  }, [values.giftCardCode]);

  return (
    <Form>
      <GiftCardForm.ModalGiftCardNumberInput>
        <input type="hidden" name="giftCardId" />
        <FormikInput
          data-qa="gift_card_code_input"
          name="giftCardCode"
          type="string"
          label={inputLabel}
          hasError={!!errors.giftCardCode}
          errorMessage={errors.giftCardCode}
          maxLength={19}
          onFocus={() => setInputLabel(ENTER_CARD_LABEL)}
          onBlur={() => setInputLabel(GIFT_CARD_CODE_LABEL)}
        />
      </GiftCardForm.ModalGiftCardNumberInput>
      {values.giftCardValue !== null && values.giftCardValue >= 0 && (
        <GiftCardForm.ModalGiftCardBalance>
          <Field name="giftCardValue">
            {({ field }) => (
              <div>
                <GiftCardForm.AvailableBalanceCopy>
                  {t(`${TP}.availableBalance`, 'Available Balance')}
                </GiftCardForm.AvailableBalanceCopy>
                <p>
                  {formatMoneyObject(
                    {
                      currency: 'USD',
                      amount: field.value,
                    },
                    {
                      showCents: false,
                      displayCurrencySymbol: true,
                    },
                  )}
                  {selectedCurrencyCode !== 'USD' && (
                    <GiftCardForm.ForeignCurrencyAmount>
                      {t(`${TP}.ForeignCurrencyAmount`, 'Approx. {amount}', {
                        amount: formatMoneyObject(
                          {
                            currency: selectedCurrencyCode,
                            amount: foreignCurrencyAmountCents,
                          },
                          {
                            showCents: true,
                            displayCurrencySymbol: true,
                          },
                        ),
                      })}
                    </GiftCardForm.ForeignCurrencyAmount>
                  )}
                </p>
              </div>
            )}
          </Field>
        </GiftCardForm.ModalGiftCardBalance>
      )}
      <GiftCardForm.ModalFooterTitle>
        <Trans
          i18nKey={`${TP}.aggreement`}
          defaults="By proceeding, you confirm that you agree to the <0>Gift Card Terms</0>."
          components={[
            <GiftCardForm.TermsLink
              href={getRedirectionPath('/gift-card-terms')}
              target="_blank"
              rel="noreferrer"
            />,
          ]}
        />
      </GiftCardForm.ModalFooterTitle>
      <GiftCardForm.Button
        disabled={!values.giftCardValue}
        loading={validating}
        data-qa="apply_gift_card_button"
        title={t(`${TP}.apply`, 'Apply')}
        onClick={submitForm}
      />
    </Form>
  );
};

GiftCardForm.TermsLink = styled.a`
  text-decoration: underline;
`;

GiftCardForm.ModalGiftCardNumberInput = styled.div`
  margin-top: 10px;
`;

GiftCardForm.ModalGiftCardBalance = styled.div`
  margin-bottom: 10px;
`;

GiftCardForm.ModalFooterTitle = styled.p`
  ${variableFontRegular({ size: '9.5px' })};
`;

GiftCardForm.ForeignCurrencyAmount = styled.span`
  ${variableFontRegular({ size: '9.5px' })};
  display: block;
`;

GiftCardForm.AvailableBalanceCopy = styled.p`
  ${variableFontRegular({ size: '9.5px' })};
  margin-bottom: 0;
`;

GiftCardForm.Button = styled(Button)`
  ${variableFontRegular({ size: '9px' })};
  height: 32px;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontRegular({ size: '9.5px' })};
  }
`;
